import React from "react";
import { Button, Space, Table, Tooltip, Typography, TableColumnsType } from "antd";
import { OrderRecalcPreviewResult, OrderVersionLineItemDto } from "../../../interfaces/OrderDetail";
import { HardwarePendingChange } from "./hooks/useOrderDetailsEdit";
import { formatCurrency } from "./quoteVersionUtils";
import { PendingChangeNote } from "./PendingChangeNote";
import { EditOutlined, InfoCircleOutlined } from "@ant-design/icons";

const CATEGORY_LABEL_MAP: Record<string, string> = {
  C: "Cover Shape",
  HW: "Basic Hardware",
  HW1: "Basic Hardware",
  HW1A: "Basic Hardware",
  HW2: "Basic Hardware",
  HW3: "Basic Hardware",
  HWA: "Basic Hardware",
  HWS: "Basic Hardware",
  HWSC: "Basic Hardware",
  MIH: "Basic Hardware",
  MIHA: "Basic Hardware",
  MICH: "Basic Hardware",
  MIHT: "Basic Hardware",
  OBST: "Custom Treatments",
  OBST1: "Custom Treatments",
  OBST2: "Custom Treatments",
  PAD: "Pads",
  PAD1: "Pads",
  PAD2: "Pads",
  DRAIN: "Drainage",
  MI1: "Miscellaneous",
  MI2: "Miscellaneous",
  MI3: "Miscellaneous",
  CDESC: "Cover Description",
  LIT: "Literature",
  RM: "Repair / Miscellaneous",
  BBQ: "BBQ Covers",
  COMM1: "Comments",
  COMM2: "Comments",
  LI: "Line Items",
};

const SECTION_ORDER: string[] = [
  "Cover Shape",
  "Basic Hardware",
  "Custom Treatments",
  "Pads",
  "Drainage",
  "Miscellaneous",
  "Cover Description",
  "Literature",
  "Repair / Miscellaneous",
  "BBQ Covers",
  "Comments",
  "Line Items",
];

function getSectionLabel(category: string): string {
  return CATEGORY_LABEL_MAP[category] ?? category;
}

function formatItemDescription(item: OrderVersionLineItemDto): string {
  if (item.category === "C") {
    return `${item.description} (Total Sq Ft= ${item.sqft ?? 0})`;
  }
  const qty = item.quantity ?? 0;
  const unit = item.unitPrice ?? 0;
  return `${item.description} (${qty}+/- @$${unit.toFixed(2)}ea)`;
}

interface TableRow {
  key: string;
  section: string;
  misc07: string | null;
  sectionRowSpan: number;
  description: string;
  rawDescription: string;
  listPrice: number | null;
  netPrice: number | null;
  lineIdcol: number | null;
  sourceLine: OrderVersionLineItemDto;
  isPending: boolean;
}

interface LineItemsTableProps {
  lineItems: OrderVersionLineItemDto[];
  productLine: string;
  editing: boolean;
  onRequestComment?: (text: string) => void;
  onEditHardware?: (line: OrderVersionLineItemDto) => void;
  pendingHardwareChanges?: HardwarePendingChange[];
  previewTotals?: OrderRecalcPreviewResult | null;
}

export const LineItemsTable: React.FC<LineItemsTableProps> = ({
  lineItems,
  productLine,
  onRequestComment,
  editing,
  onEditHardware,
  pendingHardwareChanges,
  previewTotals,
}) => {
  const groupMap = new Map<string, OrderVersionLineItemDto[]>();
  for (const item of lineItems) {
    const label = getSectionLabel(item.category);
    if (!groupMap.has(label)) groupMap.set(label, []);
    groupMap.get(label)!.push(item);
  }

  const sortedLabels = Array.from(groupMap.keys()).sort((a, b) => {
    const ai = SECTION_ORDER.indexOf(a);
    const bi = SECTION_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const dataSource: TableRow[] = [];
  for (const label of sortedLabels) {
    const items = groupMap.get(label)!;
    items.forEach((item, idx) => {
      const pendingChange = pendingHardwareChanges?.find(
        (c) => c.lineIdcol === item.lineIdcol,
      );
      const isPending = pendingChange != null;
      const isCoverRow = item.category === "C";

      // Prefer the server-computed preview once it has landed — it's authoritative (reflects
      // discounting/repricing the local approximation below can't do), while the local
      // unit price * qty math gives instant feedback before the debounced preview call
      // resolves, or as a fallback if it errored.
      let displayItem: OrderVersionLineItemDto = item;
      if (pendingChange) {
        const previewLine =
          item.lineIdcol != null
            ? previewTotals?.lines.find((l) => l.lineIdcol === item.lineIdcol)
            : undefined;
        displayItem = {
          ...item,
          description: pendingChange.description,
          quantity: pendingChange.quantity,
          unitPrice: pendingChange.price,
          listPrice: previewLine
            ? previewLine.listPrice
            : pendingChange.price != null
              ? pendingChange.price * pendingChange.quantity
              : item.listPrice,
          netPrice: previewLine
            ? previewLine.netPrice
            : pendingChange.price != null
              ? pendingChange.price * pendingChange.quantity
              : item.netPrice,
        };
      } else if (isCoverRow && previewTotals?.cover) {
        displayItem = {
          ...item,
          listPrice: previewTotals.cover.listPrice,
          netPrice: previewTotals.cover.netPrice,
        };
      }

      dataSource.push({
        key: `item-${label}-${idx}`,
        misc07: item.misc07,
        section: label,
        sectionRowSpan: idx === 0 ? items.length : 0,
        description: formatItemDescription(displayItem),
        rawDescription: displayItem.description,
        listPrice: displayItem.listPrice,
        netPrice: displayItem.netPrice,
        lineIdcol: item.lineIdcol,
        sourceLine: item,
        isPending,
      });
    });
  }

  // Summed from dataSource (not the raw lineItems prop) so the subtotal row reflects any
  // staged-but-not-yet-saved hardware change too, matching what the rows above show.
  const listSubtotal = dataSource.reduce<number | null>((acc, row) => {
    if (row.listPrice === null) return acc;
    return (acc ?? 0) + row.listPrice;
  }, null);

  const netSubtotal = dataSource.reduce<number | null>((acc, row) => {
    if (row.netPrice === null) return acc;
    return (acc ?? 0) + row.netPrice;
  }, null);

  const columns: TableColumnsType<any> = [
    {
      title: "",
      dataIndex: "section",
      key: "section",
      width: 160,
      onCell: (record: TableRow) => ({
        rowSpan: record.sectionRowSpan,
      }),
    },
    {
      title: "Details",
      dataIndex: "description",
      key: "description",
      render: (description: string, record: TableRow) => {
        const isEditableHardware = !!record.misc07?.trim();

        if (isEditableHardware) {
          if (!editing) return description;
          return (
            <Space direction="vertical" size={0}>
              <Space>
                {description}
                <Button
                  icon={<EditOutlined />}
                  onClick={() => onEditHardware?.(record.sourceLine)}
                />
              </Space>
              {record.isPending && <PendingChangeNote />}
            </Space>
          );
        }

        if (!onRequestComment || !editing) return description;
        return (
          <Space>
            {description}
            <Tooltip title="This item cannot be changed directly. Click to request a change via the comments section.">
              <Button
                icon={<InfoCircleOutlined />}
                onClick={() => {
                  onRequestComment(
                    `Please update the following item: ${record.rawDescription}`,
                  );
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "List Price",
      dataIndex: "listPrice",
      key: "listPrice",
      align: "right",
      width: 110,
      render: (price: number | null) => formatCurrency(price),
    },
    {
      title: "Net Price",
      dataIndex: "netPrice",
      key: "netPrice",
      align: "right",
      width: 110,
      render: (price: number | null) => formatCurrency(price),
    },
  ];

  const summaryRow = () => (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={2}>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1} align="right">
        <Typography.Text strong >{formatCurrency(listSubtotal)}</Typography.Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={2} align="right">
        <Typography.Text strong>{formatCurrency(netSubtotal)}</Typography.Text>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  return (
    <Table
      size="small"
      bordered
      pagination={false}
      dataSource={dataSource}
      columns={columns}
      summary={summaryRow}
      style={{ marginBottom: 12 }}
    />
  );
};
